import { Avatar, Group, Stack, Table, Tabs, Text } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatusBadge, useInfinitePage, useTableSort } from "../components/ui";
import { submissionsApi } from "../lib/api";
import { useQueryFilters } from "../lib/useQueryFilters";
import { NAV_ICONS } from "../lib/navCopy";
import { formatDate } from "../lib/format";
import { axioTokens } from "../theme";
import type { Page, Submission, SubmissionStatus } from "../types/api";

const SUBMISSION_TABS = [
  { value: "all", label: "All" },
  { value: "in_progress", label: "In progress" },
  { value: "pending_hr_review", label: "Pending HR" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Rejected" },
] as const;

type TabValue = (typeof SUBMISSION_TABS)[number]["value"];

const SUBMISSION_TAB_DEFAULT: TabValue = "all";
const SUBMISSION_FILTER_DEFAULTS = { tab: SUBMISSION_TAB_DEFAULT };
const SUBMISSION_FILTER_ALLOWED = { tab: SUBMISSION_TABS.map((item) => item.value) };
const SUBMISSION_FILTER_OPTIONS = { allowed: SUBMISSION_FILTER_ALLOWED };

export function SubmissionsPage({
  basePath = "/submissions",
  listSubmissions,
  title = "My Submissions",
  subtitle = "Track every candidate you've submitted through the hiring flow.",
  showRecruiterColumn = false,
}: {
  basePath?: string;
  listSubmissions?: (params: { page?: number; page_size?: number; status?: SubmissionStatus }) => Promise<Page<Submission>>;
  title?: string;
  subtitle?: string;
  showRecruiterColumn?: boolean;
}) {
  const navigate = useNavigate();
  const submissionFilters = useQueryFilters(SUBMISSION_FILTER_DEFAULTS, SUBMISSION_FILTER_OPTIONS);
  const tab = submissionFilters.values.tab as TabValue;

  const submissionsQuery = useInfinitePage(["submissions", basePath, tab], (page, pageSize) =>
    (listSubmissions ?? submissionsApi.list)({
      page,
      page_size: pageSize,
      status: tab === "all" ? undefined : tab,
    }),
  );

  const rows = submissionsQuery.items;

  const { sorted, sortKey, direction, toggle } = useTableSort(rows, {
    candidate: (r) => r.candidate_name?.toLowerCase() ?? "",
    job: (r) => r.job_title?.toLowerCase() ?? "",
    status: (r) => r.status,
    stage: (r) => r.current_stage?.name?.toLowerCase() ?? "",
    submitted: (r) => r.created_at,
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.submissions}
        title={title}
        subtitle={subtitle}
      />

      <FilterBar>
        <Tabs value={tab} onChange={(value) => value && submissionFilters.set({ tab: value })}>
          <Tabs.List>
            {SUBMISSION_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={submissionsQuery.isLoading}
        error={
          submissionsQuery.isError
            ? submissionsQuery.error instanceof Error
              ? submissionsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void submissionsQuery.refetch()}
        hasMore={submissionsQuery.hasMore}
        loadingMore={submissionsQuery.loadingMore}
        onLoadMore={submissionsQuery.loadMore}
        isEmpty={rows.length === 0}
        empty={{
          icon: NAV_ICONS.submissions,
          title: "No submissions in this filter",
          description: "Try another tab, or submit a candidate from an open job.",
        }}
      >
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="candidate" sortKey={sortKey} direction={direction} onSort={toggle}>
                Candidate
              </SortableTh>
              <SortableTh columnKey="job" sortKey={sortKey} direction={direction} onSort={toggle}>
                Job
              </SortableTh>
              {showRecruiterColumn && <Table.Th>Recruiter</Table.Th>}
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="stage" sortKey={sortKey} direction={direction} onSort={toggle}>
                Stage
              </SortableTh>
              <SortableTh columnKey="submitted" sortKey={sortKey} direction={direction} onSort={toggle}>
                Submitted
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sorted.map((s) => (
              <Table.Tr
                key={s.id}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`${basePath}/${s.id}`)}
              >
                <Table.Td>
                  <Group gap="sm" wrap="nowrap">
                    <Avatar color="violet" radius={axioTokens.radiusCircle} size="var(--axio-unit-32)">
                      {(s.candidate_name ?? "?")[0]?.toUpperCase() ?? "?"}
                    </Avatar>
                    <Stack gap="var(--axio-unit-1)">
                      <Text fw="var(--axio-font-weight-semibold)" size="sm" style={{ color: axioTokens.ink }}>
                        {s.candidate_name ?? "—"}
                      </Text>
                      {s.candidate_phone && (
                        <Text size="xs" c="dimmed" className="tabular">
                          {s.candidate_phone}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Stack gap="var(--axio-unit-2)">
                    <Text size="sm" fw="var(--axio-font-weight-medium)">
                      {s.job_title ?? "—"}
                    </Text>
                    {s.company_name && (
                      <Text size="xs" c="dimmed">
                        {s.company_name}
                      </Text>
                    )}
                  </Stack>
                </Table.Td>
                {showRecruiterColumn && (
                  <Table.Td>
                    <Text size="sm">{s.recruiter_name ?? "—"}</Text>
                  </Table.Td>
                )}
                <Table.Td>
                  <StatusBadge status={s.status} />
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{s.current_stage?.name ?? "—"}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed" className="tabular">
                    {formatDate(s.created_at)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
